import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Table, { TableProps } from '@cloudscape-design/components/table';
import type { AssetCategory, Asset } from '@/types/storageInventory';
import { DEFAULT_ASSET_STATUSES } from '@/types/storageInventory';

interface BulkAddPanelProps {
  settings: { sites: string[]; server_makes: string[]; network_makes: string[]; elemental_manufacturers: string[]; server_models: string[]; elemental_models: string[]; network_models: string[]; statuses: string[] };
  onBulkAdd: (category: AssetCategory, assets: Omit<Asset, 'id'>[]) => void;
  onOpenCsvImport: () => void;
  onExport: (category: AssetCategory) => void;
}

const CATEGORY_OPTIONS = [
  { label: 'Servers/Encoders/Appliances', value: 'servers' },
  { label: 'Spare Elementals', value: 'elementals' },
  { label: 'Network Devices', value: 'network_devices' },
];

interface RowData {
  key: number;
  [field: string]: unknown;
}

let rowKeyCounter = 0;

function createEmptyRow(category: AssetCategory): RowData {
  rowKeyCounter++;
  if (category === 'servers') {
    return { key: rowKeyCounter, site: '', hostname: '', make: '', model: '', service_tag: '', express_service_code: '', status: 'in storage' };
  }
  if (category === 'elementals') {
    return { key: rowKeyCounter, site: '', series: '', manufacturer: '', model: '', service_tag: '', express_service_code: '', boxed_status: 'unboxed', condition: 'new', status: 'in storage', to_be_decommissioned: false };
  }
  return { key: rowKeyCounter, site: '', hostname: '', make: '', model: '', serial_num: '', status: 'in storage', quantity: 1 };
}

export function BulkAddPanel({ settings, onBulkAdd, onOpenCsvImport, onExport }: BulkAddPanelProps) {
  const [category, setCategory] = useState<AssetCategory>('servers');
  const [rows, setRows] = useState<RowData[]>([createEmptyRow('servers'), createEmptyRow('servers'), createEmptyRow('servers')]);
  const [submitted, setSubmitted] = useState(false);
  const [csvCategory, setCsvCategory] = useState<AssetCategory>('servers');

  const handleCategoryChange = (cat: AssetCategory) => {
    setCategory(cat);
    setRows([createEmptyRow(cat), createEmptyRow(cat), createEmptyRow(cat)]);
    setSubmitted(false);
  };

  const updateRow = (index: number, field: string, value: unknown) => {
    setRows((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addRows = (count: number) => {
    const newRows = Array.from({ length: count }, () => createEmptyRow(category));
    setRows((prev) => [...prev, ...newRows]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    // Filter out empty rows (rows where all required fields are empty)
    const filledRows = rows.filter((row) => {
      if (category === 'servers') return row.site && row.make && row.model && row.service_tag;
      if (category === 'elementals') return row.site && row.series && row.manufacturer && row.model && row.service_tag;
      return row.site && row.make && row.model && row.serial_num;
    });

    if (filledRows.length === 0) return;

    const assets = filledRows.map((row) => {
      const { key, ...data } = row;
      // Auto-fill hostname if blank for servers
      if (category === 'servers' && (!data.hostname || String(data.hostname).trim() === '')) {
        data.hostname = 'no_hostname';
      }
      return data as Omit<Asset, 'id'>;
    });

    onBulkAdd(category, assets);
    setSubmitted(true);
    setRows([createEmptyRow(category), createEmptyRow(category), createEmptyRow(category)]);
    setTimeout(() => setSubmitted(false), 4000);
  };

  const filledCount = rows.filter((row) => {
    if (category === 'servers') return row.site && row.hostname;
    if (category === 'elementals') return row.site && row.series;
    return row.site && row.make;
  }).length;

  const siteOptions = settings.sites.map((s) => ({ label: s, value: s }));
  const makeOptions = (category === 'servers' ? settings.server_makes : category === 'elementals' ? settings.elemental_manufacturers : settings.network_makes).map((m) => ({ label: m, value: m }));
  const modelOptions = (category === 'servers' ? (settings.server_models || []) : category === 'elementals' ? (settings.elemental_models || []) : (settings.network_models || [])).slice().sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((m) => ({ label: m, value: m }));

  const getIdx = (item: RowData) => rows.findIndex((r) => r.key === item.key);

  const renderServerColumns = () => [
    { id: 'num', header: '#', cell: (item: RowData) => getIdx(item) + 1, width: 40 },
    { id: 'site', header: 'Site *', cell: (item: RowData) => (
      <Select selectedOption={item.site ? { label: String(item.site), value: String(item.site) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'site', detail.selectedOption.value)} options={siteOptions} placeholder="Site" filteringType="auto" expandToViewport />
    ), width: 150 },
    { id: 'hostname', header: 'Hostname', cell: (item: RowData) => (
      <Input value={String(item.hostname || '')} onChange={({ detail }) => updateRow(getIdx(item), 'hostname', detail.value)} placeholder="hostname" />
    ), width: 160 },
    { id: 'make', header: 'Make *', cell: (item: RowData) => (
      <Select selectedOption={item.make ? { label: String(item.make), value: String(item.make) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'make', detail.selectedOption.value)} options={makeOptions} placeholder="Make" filteringType="auto" expandToViewport />
    ), width: 120 },
    { id: 'model', header: 'Model *', cell: (item: RowData) => (
      <Select selectedOption={item.model ? { label: String(item.model), value: String(item.model) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'model', detail.selectedOption.value)} options={modelOptions} placeholder="Model" filteringType="auto" expandToViewport />
    ), width: 130 },
    { id: 'service_tag', header: 'Service Tag *', cell: (item: RowData) => (
      <Input value={String(item.service_tag || '')} onChange={({ detail }) => updateRow(getIdx(item), 'service_tag', detail.value)} placeholder="ABC123" />
    ), width: 130 },
    { id: 'express_service_code', header: 'Express Code', cell: (item: RowData) => (
      <Input value={String(item.express_service_code || '')} onChange={({ detail }) => updateRow(getIdx(item), 'express_service_code', detail.value)} placeholder="1234567" />
    ), width: 120 },
    { id: 'status', header: 'Status', cell: (item: RowData) => (
      <Select selectedOption={{ label: String(item.status || 'in storage'), value: String(item.status || 'in storage') }} onChange={({ detail }) => updateRow(getIdx(item), 'status', detail.selectedOption.value)} options={(settings.statuses && settings.statuses.length > 0 ? settings.statuses : DEFAULT_ASSET_STATUSES).map(s => ({ label: s, value: s }))} expandToViewport />
    ), width: 140 },
    { id: 'remove', header: '', cell: (item: RowData) => (
      <Button variant="icon" iconName="close" onClick={() => removeRow(getIdx(item))} ariaLabel="Remove row" />
    ), width: 50 },
  ];

  const renderElementalColumns = () => [
    { id: 'num', header: '#', cell: (item: RowData) => getIdx(item) + 1, width: 40 },
    { id: 'site', header: 'Site *', cell: (item: RowData) => (
      <Select selectedOption={item.site ? { label: String(item.site), value: String(item.site) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'site', detail.selectedOption.value)} options={siteOptions} placeholder="Site" filteringType="auto" expandToViewport />
    ), width: 150 },
    { id: 'series', header: 'Series *', cell: (item: RowData) => (
      <Input value={String(item.series || '')} onChange={({ detail }) => updateRow(getIdx(item), 'series', detail.value)} placeholder="800" />
    ), width: 80 },
    { id: 'manufacturer', header: 'Mfr *', cell: (item: RowData) => (
      <Select selectedOption={item.manufacturer ? { label: String(item.manufacturer), value: String(item.manufacturer) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'manufacturer', detail.selectedOption.value)} options={makeOptions} placeholder="Mfr" filteringType="auto" expandToViewport />
    ), width: 100 },
    { id: 'model', header: 'Model *', cell: (item: RowData) => (
      <Select selectedOption={item.model ? { label: String(item.model), value: String(item.model) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'model', detail.selectedOption.value)} options={modelOptions} placeholder="Model" filteringType="auto" expandToViewport />
    ), width: 120 },
    { id: 'service_tag', header: 'Service Tag *', cell: (item: RowData) => (
      <Input value={String(item.service_tag || '')} onChange={({ detail }) => updateRow(getIdx(item), 'service_tag', detail.value)} placeholder="ABC123" />
    ), width: 120 },
    { id: 'express_service_code', header: 'Express Code', cell: (item: RowData) => (
      <Input value={String(item.express_service_code || '')} onChange={({ detail }) => updateRow(getIdx(item), 'express_service_code', detail.value)} placeholder="1234567" />
    ), width: 120 },
    { id: 'boxed_status', header: 'Boxed', cell: (item: RowData) => (
      <Select selectedOption={{ label: String(item.boxed_status || 'unboxed'), value: String(item.boxed_status || 'unboxed') }} onChange={({ detail }) => updateRow(getIdx(item), 'boxed_status', detail.selectedOption.value)} options={[{ label: 'boxed', value: 'boxed' }, { label: 'unboxed', value: 'unboxed' }]} expandToViewport />
    ), width: 110 },
    { id: 'condition', header: 'Condition', cell: (item: RowData) => (
      <Select selectedOption={{ label: String(item.condition || 'new'), value: String(item.condition || 'new') }} onChange={({ detail }) => updateRow(getIdx(item), 'condition', detail.selectedOption.value)} options={[{ label: 'new', value: 'new' }, { label: 'used', value: 'used' }, { label: 'refurbished', value: 'refurbished' }]} expandToViewport />
    ), width: 120 },
    { id: 'status', header: 'Status', cell: (item: RowData) => (
      <Select selectedOption={{ label: String(item.status || 'in storage'), value: String(item.status || 'in storage') }} onChange={({ detail }) => updateRow(getIdx(item), 'status', detail.selectedOption.value)} options={(settings.statuses && settings.statuses.length > 0 ? settings.statuses : DEFAULT_ASSET_STATUSES).map(s => ({ label: s, value: s }))} expandToViewport />
    ), width: 140 },
    { id: 'remove', header: '', cell: (item: RowData) => (
      <Button variant="icon" iconName="close" onClick={() => removeRow(getIdx(item))} ariaLabel="Remove row" />
    ), width: 50 },
  ];

  const renderNetworkColumns = () => [
    { id: 'num', header: '#', cell: (item: RowData) => getIdx(item) + 1, width: 40 },
    { id: 'site', header: 'Site *', cell: (item: RowData) => (
      <Select selectedOption={item.site ? { label: String(item.site), value: String(item.site) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'site', detail.selectedOption.value)} options={siteOptions} placeholder="Site" filteringType="auto" expandToViewport />
    ), width: 150 },
    { id: 'make', header: 'Make *', cell: (item: RowData) => (
      <Select selectedOption={item.make ? { label: String(item.make), value: String(item.make) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'make', detail.selectedOption.value)} options={makeOptions} placeholder="Make" filteringType="auto" expandToViewport />
    ), width: 120 },
    { id: 'model', header: 'Model *', cell: (item: RowData) => (
      <Select selectedOption={item.model ? { label: String(item.model), value: String(item.model) } : null} onChange={({ detail }) => updateRow(getIdx(item), 'model', detail.selectedOption.value)} options={modelOptions} placeholder="Model" filteringType="auto" expandToViewport />
    ), width: 150 },
    { id: 'serial_num', header: 'Serial # *', cell: (item: RowData) => (
      <Input value={String(item.serial_num || '')} onChange={({ detail }) => updateRow(getIdx(item), 'serial_num', detail.value)} placeholder="SN123" />
    ), width: 130 },
    { id: 'status', header: 'Status', cell: (item: RowData) => (
      <Select selectedOption={{ label: String(item.status || 'in storage'), value: String(item.status || 'in storage') }} onChange={({ detail }) => updateRow(getIdx(item), 'status', detail.selectedOption.value)} options={(settings.statuses && settings.statuses.length > 0 ? settings.statuses : DEFAULT_ASSET_STATUSES).map(s => ({ label: s, value: s }))} expandToViewport />
    ), width: 140 },
    { id: 'quantity', header: 'Qty', cell: (item: RowData) => (
      <Input type="number" value={String(item.quantity || 1)} onChange={({ detail }) => updateRow(getIdx(item), 'quantity', parseInt(detail.value) || 1)} />
    ), width: 70 },
    { id: 'remove', header: '', cell: (item: RowData) => (
      <Button variant="icon" iconName="close" onClick={() => removeRow(getIdx(item))} ariaLabel="Remove row" />
    ), width: 50 },
  ];

  const getColumns = () => {
    switch (category) {
      case 'servers': return renderServerColumns();
      case 'elementals': return renderElementalColumns();
      case 'network_devices': return renderNetworkColumns();
    }
  };

  return (
    <SpaceBetween size="l">
      {/* Info section */}
      <Alert type="warning">
        <SpaceBetween size="xxs">
          <Box variant="p"><strong>Bulk Add</strong> — Add multiple assets at once. Select a category, fill in the rows, and submit.</Box>
          <Box variant="p">1. Choose category &nbsp;→&nbsp; 2. Fill rows (* = required) &nbsp;→&nbsp; 3. Click "Add all"</Box>
          <Box variant="p">💡 Don't see a model, make, or site? Head to the <strong>Settings</strong> tab to add new options.</Box>
        </SpaceBetween>
      </Alert>

      {submitted && (
        <Alert type="success" dismissible onDismiss={() => setSubmitted(false)}>
          Assets added successfully!
        </Alert>
      )}

      <Container header={
        <Header
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => addRows(5)}>+ Add 5 rows</Button>
              <Button onClick={() => addRows(10)}>+ Add 10 rows</Button>
              <Button variant="primary" onClick={handleSubmit} disabled={filledCount === 0}>
                Add all ({filledCount} filled)
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="horizontal" size="m">
            <span>Category:</span>
            <Select
              selectedOption={CATEGORY_OPTIONS.find((o) => o.value === category) || null}
              onChange={({ detail }) => handleCategoryChange(detail.selectedOption.value as AssetCategory)}
              options={CATEGORY_OPTIONS}
            />
          </SpaceBetween>
        </Header>
      }>
        <div style={{ overflow: 'visible' }}>
          <Table
            columnDefinitions={getColumns() as unknown as TableProps.ColumnDefinition<RowData>[]}
            items={rows}
            variant="embedded"
            stripedRows
            trackBy="key"
          />
        </div>
      </Container>

      {/* CSV Import section */}
      {import.meta.env.DEV && (
        <Container header={<Header variant="h2">Import from CSV</Header>}>
          <SpaceBetween size="m">
            <Alert type="warning">
              <strong>⚠️ CSV import adds rows to your inventory.</strong><br />With the local backend enabled it persists to the local SQLite database via the proxy. Export existing data first to get the right column headers. A backup is automatically saved before each import (last 5 kept).
            </Alert>
            <SpaceBetween size="xs">
              <Box variant="p"><strong>Steps:</strong></Box>
              <Box variant="p">1. Select the category and click "Export Current Data" to download a CSV with the correct column headers</Box>
              <Box variant="p">2. Open the CSV file and add new rows at the bottom (keep the header row)</Box>
              <Box variant="p">3. Click "Bulk Import CSV" to upload and import the modified file</Box>
            </SpaceBetween>
            <SpaceBetween direction="horizontal" size="m">
              <Select
                selectedOption={CATEGORY_OPTIONS.find((o) => o.value === csvCategory) || null}
                onChange={({ detail }) => setCsvCategory(detail.selectedOption.value as AssetCategory)}
                options={CATEGORY_OPTIONS}
                expandToViewport
              />
              <Button iconName="download" onClick={() => onExport(csvCategory)}>
                Export Current Data
              </Button>
              <Button iconName="upload" onClick={onOpenCsvImport}>
                Bulk Import CSV
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
